import { FunctionComponent, useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@apollo/client";
import backArrow from "../../assets/task/back_arrow.svg";
import AgencyConfigTab from "../../components/migration/AgencyConfigTab";
import AgencyInfoTab from "../../components/migration/AgencyInfoTab";
import Loader from "../../components/common/Loader";
import { MIGRATION_AGENCY_BY_ID, EMPLOYEES_BY_AGENCY_ID, MIGRATION_ENTITIES_BY_AGENCY } from "../../api/query";
import { useAuth } from "../../context/AuthContext";

interface TeamMember {
    id: number;
    name: string;
    initials: string;
    assignedCount: number;
}

interface Record {
    id: number;
    name: string;
    status: "Completed" | "In Progress" | "Issues Found" | "Not Started";
    assignedTo: string;
    issues?: string;
}

const SortIcon = ({ active, dir }: { active: boolean; dir: "asc" | "desc" }) => (
    <svg className="w-3.5 h-3.5 shrink-0" viewBox="0 0 16 16" fill="none">
        <path d="M8 3L5 7h6L8 3z" fill={active && dir === "asc" ? "currentColor" : "#9CA3AF"} />
        <path d="M8 13l3-4H5l3 4z" fill={active && dir === "desc" ? "currentColor" : "#9CA3AF"} />
    </svg>
);

const AgencyMigrationDetail: FunctionComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const agencyData = location.state?.agency;
    const savedActiveTab = location.state?.activeTab;
    const savedSelectedMember = location.state?.selectedMember;

    const { userInfo } = useAuth();
    const organizationID = userInfo?.agencies?.id || 0;

    const [activeTab, setActiveTab] = useState<"agencyConfig" | "agencyInfo" | "clients" | "caregivers" | "employees" | "prospects">(savedActiveTab || "agencyConfig");
    const [selectedMember, setSelectedMember] = useState<number | null>(savedSelectedMember !== undefined ? savedSelectedMember : null);

    // Fetch real agency data
    const { data: agencyDetailData, loading: agencyLoading, refetch: refetchAgency } = useQuery(MIGRATION_AGENCY_BY_ID, {
        variables: { agency_id: parseInt(agencyData?.id) },
        fetchPolicy: "no-cache",
        skip: !agencyData?.id,
    });

    const { data: employeesData, loading: employeesLoading } = useQuery(EMPLOYEES_BY_AGENCY_ID, {
        variables: { agency_id: parseInt(organizationID) },
        fetchPolicy: "no-cache",
        skip: !organizationID,
    });

    const realAgency = agencyDetailData?.migration_agency_by_id?.agency;
    const allEmployees: any[] = employeesData?.employees_by_agency_id?.employees || [];

    // Fetch all entities once — split by type for each tab
    const { data: entitiesData, loading: entitiesLoading, refetch: refetchEntities } = useQuery(MIGRATION_ENTITIES_BY_AGENCY, {
        variables: { agency_id: parseInt(agencyData?.id) },
        fetchPolicy: "no-cache",
        skip: !agencyData?.id,
    });

    const allEntities: any[] = entitiesData?.migration_entities_by_agency?.entities || [];
    const clients = allEntities.filter(e => e.entity_type === "client");
    const caregivers = allEntities.filter(e => e.entity_type === "caregiver");
    const employees = allEntities.filter(e => e.entity_type === "employee");
    const prospects = allEntities.filter(e => e.entity_type === "prospect");

    // Build team members from assigned_team_members + employee lookup
    const teamMembers: TeamMember[] = (realAgency?.assigned_team_members || []).map((m: any) => {
        const emp = allEmployees.find((e: any) => Number(e.employee_id) === Number(m.member_id));
        const name = emp ? `${emp.first_name} ${emp.last_name}` : `Member ${m.member_id}`;
        const initials = emp
            ? `${emp.first_name?.[0] || ""}${emp.last_name?.[0] || ""}`.toUpperCase()
            : String(m.member_id).slice(-2);
        return { id: Number(m.member_id), name, initials, assignedCount: 0 };
    });

    const getCurrentRecords = () => {
        let records: any[] = [];
        switch (activeTab) {
            case "clients": records = clients; break;
            case "caregivers": records = caregivers; break;
            case "employees": records = employees; break;
            case "prospects": records = prospects; break;
            default: return [];
        }
        if (selectedMember !== null) {
            return records.filter(e => Number(e.member_id) === selectedMember);
        }
        return records;
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "closed":          return "bg-green-100 text-green-700 border-green-300";
            case "verified":        return "bg-teal-100 text-teal-700 border-teal-300";
            case "in_progress":     return "bg-blue-100 text-blue-700 border-blue-300";
            case "has_issue":       return "bg-orange text-orange-700 border-orange";
            case "not_started":     
            default:                return "bg-gray-100 text-gray-600 border-gray-300";
        }
    };

    const [sortKey, setSortKey] = useState<"name" | "status" | "assignedTo" | null>(null);
    const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

    const handleSort = (key: "name" | "status" | "assignedTo") => {
        if (sortKey === key) {
            setSortDir(d => d === "asc" ? "desc" : "asc");
        } else {
            setSortKey(key);
            setSortDir("asc");
        }
    };

    const getSortValue = (entity: any, key: "name" | "status" | "assignedTo") => {
        if (key === "name") return [entity.last_name, [entity.first_name, entity.middle_name].filter(Boolean).join(" ")].filter(Boolean).join(", ").toLowerCase();
        if (key === "status") return (entity.progress_status || "").toLowerCase();
        if (key === "assignedTo") return (teamMembers.find(m => m.id === Number(entity.member_id))?.name || "").toLowerCase();
        return "";
    };

    const baseRecords = getCurrentRecords();
    const filteredRecords = sortKey
        ? [...baseRecords].sort((a, b) => {
            const av = getSortValue(a, sortKey);
            const bv = getSortValue(b, sortKey);
            return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
        })
        : baseRecords;

    const totalRecords = (realAgency?.total_clients ?? 0) + (realAgency?.total_caregivers ?? 0) + (realAgency?.total_employees ?? 0);

    return (
        <div className="w-full relative overflow-hidden flex flex-col items-start justify-start min-w-[360px] max-w-[100%] leading-[normal] tracking-[normal] text-left text-xl text-colors-text-black font-body-xs-semi-bold">
            <div className="self-stretch bg-gray overflow-hidden flex flex-col items-center justify-start relative gap-[24px] min-h-[913px] max-w-full">
                <div className="w-full flex flex-col items-start justify-start py-40 px-20 mq1490:px-[30px] mq575:px-5 box-border gap-[24px] max-w-[100%]">
                    {/* Header */}
                    <div className="self-stretch flex flex-row items-start justify-between gap-[20px] max-w-full mq825:flex-col">
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-row items-center gap-[5px]">
                                <img
                                    className="w-6 relative h-6 overflow-hidden shrink-0 cursor-pointer dark-white-img"
                                    alt=""
                                    src={backArrow}
                                    onClick={() => navigate('/migration_tracker')}
                                />
                                <h3 className="m-0 relative text-inherit font-bold font-inherit dark:text-white">
                                    {realAgency?.agency_name || agencyData?.agency_name || "Agency"}
                                </h3>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-300 m-0 ml-8">
                                {(realAgency?.total_clients ?? agencyData?.total_clients ?? 0) +
                                 (realAgency?.total_caregivers ?? agencyData?.total_caregivers ?? 0) +
                                 (realAgency?.total_employees ?? agencyData?.total_employees ?? 0)} total records
                            </p>
                        </div>
                        <div className="flex items-center gap-4">
                            <button
                                className="bg-orange-btn-dv text-white px-6 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
                                onClick={() => navigate('/agency_issues', { state: { agency: agencyData, teamMembers } })}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                                </svg>
                                Issues
                            </button>
                            <button 
                                className="plus-task-btn  text-white px-6 py-2 rounded-lg text-sm font-semibold flex items-center gap-2"
                                onClick={() => navigate('/manage_assignments', { state: { agency: agencyData } })}
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                Manage Assignments
                            </button>
                            {/* <div className="relative w-16 h-16">
                                <svg className="w-16 h-16 transform -rotate-90">
                                    <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="none" className="text-gray-200 dark:text-gray-700" />
                                    <circle cx="32" cy="32" r="28" stroke="currentColor" strokeWidth="4" fill="none" strokeDasharray={`${(agencyData?.progress || 28) * 1.76} ${100 * 1.76}`} className="text-teal-500" />
                                </svg>
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <span className="text-sm font-bold text-gray-900 dark:text-white">{agencyData?.progress || 28}%</span>
                                </div>
                            </div> */}
                        </div>
                    </div>

                    {/* Assigned Team Members */}
                    <div className="w-full bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                        <h4 className="text-base font-bold text-gray-900 dark:text-white mb-4">Assigned Team Members</h4>
                        {(agencyLoading || employeesLoading) ? <Loader /> : (
                        <div className="flex gap-4 flex-wrap">
                            <div
                                className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                                    selectedMember === null
                                        ? "bg-blue-50 border-blue-500 dark:bg-blue-900/30"
                                        : "bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 hover:border-gray-300"
                                }`}
                                onClick={() => setSelectedMember(null)}
                            >
                                <div className="w-10 h-10 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center">
                                    All
                                </div>
                                <div>
                                    <p className="text-sm font-semibold text-gray-900 dark:text-white m-0">All Members</p>
                                    <p className="text-xs text-gray-600 dark:text-gray-400 m-0">{totalRecords} assigned</p>
                                </div>
                            </div>
                            {teamMembers.map((member) => (
                                <div
                                    key={member.id}
                                    className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 cursor-pointer transition-all ${
                                        selectedMember === member.id
                                            ? "bg-blue-50 border-blue-500 dark:bg-blue-900/30"
                                            : "bg-gray-50 border-gray-200 dark:bg-gray-700 dark:border-gray-600 hover:border-gray-300"
                                    }`}
                                    onClick={() => setSelectedMember(member.id)}
                                >
                                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white text-sm font-semibold flex items-center justify-center">
                                        {member.initials}
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold text-gray-900 dark:text-white m-0">{member.name}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                        )}
                    </div>

                    {/* Tabs */}
                    <div className="w-full bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
                        <div className="flex border-b border-gray-200 dark:border-gray-700 gap-2">
                            <button
                                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                                    activeTab === "agencyConfig"
                                        ? "bg-blue-600 text-white blueboder-active"
                                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                }`}
                                onClick={() => setActiveTab("agencyConfig")}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    Agency Configuration
                                </div>
                            </button>
                            <button
                                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                                    activeTab === "agencyInfo"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                }`}
                                onClick={() => setActiveTab("agencyInfo")}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                    Agency Info
                                </div>
                            </button>
                            <button
                                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                                    activeTab === "clients"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                }`}
                                onClick={() => setActiveTab("clients")}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                                    </svg>
                                    Clients ({entitiesLoading ? "..." : (selectedMember !== null ? clients.filter(e => Number(e.member_id) === selectedMember).length : clients.length)})
                                </div>
                            </button>
                            <button
                                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                                    activeTab === "caregivers"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                }`}
                                onClick={() => setActiveTab("caregivers")}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                    Caregivers ({entitiesLoading ? "..." : (selectedMember !== null ? caregivers.filter(e => Number(e.member_id) === selectedMember).length : caregivers.length)})
                                </div>
                            </button>
                            <button
                                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                                    activeTab === "employees"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                }`}
                                onClick={() => setActiveTab("employees")}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Employees ({entitiesLoading ? "..." : (selectedMember !== null ? employees.filter(e => Number(e.member_id) === selectedMember).length : employees.length)})
                                </div>
                            </button>
                            <button
                                className={`flex-1 px-6 py-4 text-sm font-semibold transition-colors ${
                                    activeTab === "prospects"
                                        ? "bg-blue-600 text-white"
                                        : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                                }`}
                                onClick={() => setActiveTab("prospects")}
                            >
                                <div className="flex items-center justify-center gap-2">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                    </svg>
                                    Prospects ({entitiesLoading ? "..." : (selectedMember !== null ? prospects.filter(e => Number(e.member_id) === selectedMember).length : prospects.length)})
                                </div>
                            </button>
                        </div>

                        {/* Agency Configuration Tab Content */}
                        {activeTab === "agencyConfig" ? (
                            <AgencyConfigTab agencyId={agencyData?.id} onSaveSuccess={refetchAgency} onSyncSuccess={refetchEntities} />
                        ) : activeTab === "agencyInfo" ? (
                            <AgencyInfoTab agencyId={agencyData?.id} />
                        ) : (
                            /* Table for other tabs */
                            <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 dark:bg-gray-700">
                                    <tr>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 dark:hover:text-white"
                                            onClick={() => handleSort("name")}
                                        >
                                            <span className="flex items-center gap-1">
                                                Name
                                                <SortIcon active={sortKey === "name"} dir={sortDir} />
                                            </span>
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 dark:hover:text-white"
                                            onClick={() => handleSort("status")}
                                        >
                                            <span className="flex items-center gap-1">
                                                Status
                                                <SortIcon active={sortKey === "status"} dir={sortDir} />
                                            </span>
                                        </th>
                                        <th
                                            className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider cursor-pointer select-none hover:text-gray-900 dark:hover:text-white"
                                            onClick={() => handleSort("assignedTo")}
                                        >
                                            <span className="flex items-center gap-1">
                                                Assigned To
                                                <SortIcon active={sortKey === "assignedTo"} dir={sortDir} />
                                            </span>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                    {entitiesLoading ? (
                                        <tr><td colSpan={3} className="px-6 py-8 text-center"><Loader /></td></tr>
                                    ) : filteredRecords.map((entity) => (
                                        <tr
                                            key={entity.id}
                                            className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                                            onClick={() => navigate('/record_migration_detail', {
                                                state: {
                                                    record: entity,
                                                    recordType: activeTab.slice(0, -1),
                                                    agency: agencyData,
                                                    agency_type: realAgency?.agency_type || agencyData?.agency_type || "",
                                                    activeTab,
                                                    selectedMember,
                                                    entities: filteredRecords,
                                                }
                                            })}
                                        >
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <p className="text-sm font-medium text-gray-900 dark:text-white m-0">
                                                    {[entity.last_name, [entity.first_name, entity.middle_name].filter(Boolean).join(" ")].filter(Boolean).join(", ")}
                                                </p>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getStatusColor(entity.progress_status)}`}>
                                                    {entity.progress_status?.replace(/_/g, " ")}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <p className="text-sm text-gray-600 dark:text-gray-300 m-0">
                                                    {entity.member_id
                                                        ? teamMembers.find(m => m.id === Number(entity.member_id))?.name || entity.member_id
                                                        : "-"}
                                                </p>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            {filteredRecords.length === 0 && (
                                <div className="text-center py-12">
                                    <p className="text-gray-500 dark:text-gray-400">No records found</p>
                                </div>
                            )}
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgencyMigrationDetail;
